<script setup lang="ts">
import { useLocale } from '@/composables/useLocale';
import type { Locale } from '@qb/shared';

const { locale, change } = useLocale();

/**
 * Each language is labelled in its OWN native script — the international
 * convention for language switchers. This way a user who reads only Russian
 * can find "Русский" without needing to know English.
 */
const LABELS: Record<Locale, string> = {
  en: 'English',
  he: 'עברית',
  pt: 'Português',
  ru: 'Русский',
};

function onChange(e: Event) {
  const target = e.target as HTMLSelectElement;
  change(target.value as Locale);
}
</script>

<template>
  <label class="qb-locale-switcher">
    <span class="qb-sr-only">Language</span>
    <select :value="locale" class="qb-locale-switcher__select" @change="onChange">
      <option v-for="(label, code) in LABELS" :key="code" :value="code">
        {{ label }}
      </option>
    </select>
  </label>
</template>

<style scoped>
.qb-locale-switcher {
  display: inline-flex;
  align-items: center;
}

.qb-locale-switcher__select {
  font-family: var(--qb-font-family);
  font-size: var(--qb-font-size-sm);
  font-weight: var(--qb-font-weight-medium);
  color: var(--qb-aside-fg);
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--qb-radius-md);
  padding: 6px 28px 6px 12px;
  min-width: 140px;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  /* Custom chevron — works in both LTR and RTL via background-position-x. */
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path d='M2 4l4 4 4-4' stroke='%23ffffff' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 8px center;
  transition: border-color var(--qb-transition-fast);
}

[dir='rtl'] .qb-locale-switcher__select {
  padding: 6px 12px 6px 28px;
  background-position: left 8px center;
}

.qb-locale-switcher__select:hover,
.qb-locale-switcher__select:focus {
  border-color: rgba(255, 255, 255, 0.35);
}

.qb-locale-switcher__select option {
  color: var(--qb-text-default);
  background: var(--qb-surface-0);
}
</style>
